import { supabase } from './supabase';
import { Post, PostMedia, PostHashtag, PostUserTag } from '../types';

export const createPost = async (
  userId: string,
  caption: string,
  location?: string
): Promise<string> => {
  const { data, error } = await supabase
    .from('posts')
    .insert([
      {
        user_id: userId,
        caption,
        location: location || null,
      }
    ])
    .select('id')
    .single();

  if (error) throw error;
  return data.id;
};

export const deletePost = async (postId: string, userId: string): Promise<void> => {
  // First, get all media files associated with the post to delete from storage
  const { data: mediaFiles } = await supabase
    .from('post_media')
    .select('media_url')
    .eq('post_id', postId);

  // Delete the post (this will cascade delete all related records due to foreign key constraints)
  const { error: deleteError } = await supabase
    .from('posts')
    .delete()
    .eq('id', postId)
    .eq('user_id', userId); // Ensure user can only delete their own posts

  if (deleteError) throw deleteError;

  // Delete media files from storage (if they exist and are not data URLs)
  if (mediaFiles && mediaFiles.length > 0) {
    for (const media of mediaFiles) {
      if (media.media_url && !media.media_url.startsWith('data:') && media.media_url.includes('supabase')) {
        try {
          // Extract file path from URL
          const url = new URL(media.media_url);
          const pathParts = url.pathname.split('/');
          const filePath = pathParts.slice(pathParts.indexOf('media') + 1).join('/');
          
          if (filePath) {
            await supabase.storage
              .from('media')
              .remove([filePath]);
          }
        } catch (storageError) {
          console.warn('Failed to delete media file from storage:', storageError);
          // Don't throw error for storage deletion failures
        }
      }
    }
  }
};

export const addPostMedia = async (
  postId: string,
  mediaUrl: string,
  mediaType: 'image' | 'video',
  orderIndex: number,
  width?: number,
  height?: number
): Promise<void> => {
  const { error } = await supabase
    .from('post_media')
    .insert([
      {
        post_id: postId,
        media_url: mediaUrl,
        media_type: mediaType,
        order_index: orderIndex,
        width,
        height,
      }
    ]);

  if (error) throw error;
};

export const addPostHashtags = async (
  postId: string,
  hashtags: string[]
): Promise<void> => {
  if (hashtags.length === 0) return;

  const hashtagRecords = hashtags.map(hashtag => ({
    post_id: postId,
    hashtag: hashtag.toLowerCase(),
  }));

  const { error } = await supabase
    .from('post_hashtags')
    .insert(hashtagRecords);

  if (error) throw error;
};

export const addPostUserTags = async (
  postId: string,
  userIds: string[]
): Promise<void> => {
  if (userIds.length === 0) return;

  const userTagRecords = userIds.map(userId => ({
    post_id: postId,
    tagged_user_id: userId,
  }));

  const { error } = await supabase
    .from('post_user_tags')
    .insert(userTagRecords);

  if (error) throw error;
};

export const getFeedPosts = async (
  userId: string,
  limit: number = 20,
  offset: number = 0
): Promise<Post[]> => {
  try {
    const { data, error } = await supabase
      .from('posts')
      .select(`
        *,
        profiles:user_id (
          id,
          username,
          full_name,
          avatar_url
        ),
        post_media (
          id,
          media_url,
          media_type,
          order_index,
          width,
          height
        ),
        post_hashtags (
          id,
          hashtag
        ),
        post_user_tags (
          id,
          tagged_user_id,
          x_position,
          y_position,
          profiles:tagged_user_id (
            id,
            username,
            full_name,
            avatar_url
          )
        ),
        post_likes (
          id,
          user_id
        ),
        post_comments (
          id,
          user_id,
          content,
          created_at,
          profiles:user_id (
            id,
            username,
            full_name,
            avatar_url
          )
        )
      `)
      .order('created_at', { ascending: false })
      .range(offset, offset + limit - 1);

    if (error) {
      console.error('Database query error:', error);
      return [];
    }

    if (!data || data.length === 0) {
      console.log('No posts found in database');
      return [];
    }

    // Transform the data to match our Post interface
    return data.map(post => {
      // Handle case where profiles might be null
      const profile = post.profiles || {
        id: post.user_id,
        username: 'unknown',
        full_name: 'Unknown User',
        avatar_url: 'https://images.pexels.com/photos/2253275/pexels-photo-2253275.jpeg?auto=compress&cs=tinysrgb&w=300'
      };

      return {
        id: post.id,
        user_id: post.user_id,
        user: {
          id: profile.id,
          username: profile.username,
          displayName: profile.full_name || profile.username,
          profileImage: profile.avatar_url || 'https://images.pexels.com/photos/2253275/pexels-photo-2253275.jpeg?auto=compress&cs=tinysrgb&w=300',
          bio: '',
          followerCount: 0,
          followingCount: 0,
          postCount: 0,
        },
        caption: post.caption || '',
        location: post.location,
        created_at: post.created_at,
        updated_at: post.updated_at,
        media: (post.post_media || [])
          .filter((media: any) => !media.media_url.includes('example.com'))
          .sort((a: any, b: any) => a.order_index - b.order_index),
        hashtags: post.post_hashtags || [],
        user_tags: (post.post_user_tags || []).map((tag: any) => ({
          ...tag,
          tagged_user: tag.profiles ? {
            id: tag.profiles.id,
            username: tag.profiles.username,
            displayName: tag.profiles.full_name || tag.profiles.username,
            profileImage: tag.profiles.avatar_url || 'https://images.pexels.com/photos/2253275/pexels-photo-2253275.jpeg?auto=compress&cs=tinysrgb&w=300',
            bio: '',
            followerCount: 0,
            followingCount: 0,
            postCount: 0,
          } : null
        })),
        likes: post.post_likes || [],
        comments: (post.post_comments || []).map((comment: any) => ({
          ...comment,
          user: comment.profiles ? {
            id: comment.profiles.id,
            username: comment.profiles.username,
            displayName: comment.profiles.full_name || comment.profiles.username,
            profileImage: comment.profiles.avatar_url || 'https://images.pexels.com/photos/2253275/pexels-photo-2253275.jpeg?auto=compress&cs=tinysrgb&w=300',
            bio: '',
            followerCount: 0,
            followingCount: 0,
            postCount: 0,
          } : null
        })),
        like_count: (post.post_likes || []).length,
        comment_count: (post.post_comments || []).length,
        is_liked: (post.post_likes || []).some((like: any) => like.user_id === userId),
        is_saved: false, // This would require a separate saved_posts table
      };
    });
  } catch (err) {
    console.error('Error in getFeedPosts:', err);
    return [];
  }
};

export const likePost = async (postId: string, userId: string): Promise<void> => {
  const { error } = await supabase
    .from('post_likes')
    .insert([{ post_id: postId, user_id: userId }]);

  if (error) throw error;
};

export const unlikePost = async (postId: string, userId: string): Promise<void> => {
  const { error } = await supabase
    .from('post_likes')
    .delete()
    .eq('post_id', postId)
    .eq('user_id', userId);

  if (error) throw error;
};

export const addComment = async (
  postId: string,
  userId: string,
  content: string
): Promise<void> => {
  const { error } = await supabase
    .from('post_comments')
    .insert([
      {
        post_id: postId,
        user_id: userId,
        content,
      }
    ]);

  if (error) throw error;
};